import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

/** Layout box of a ▲/▼ with a small text past it, relative to the triangle centre. */
export function triangleTextBox(fontSize: number, text: string, measure: (text: string, font: string) => number): { w: number; h: number; glyphFromNear: number } {
  const near = fontSize * 0.55;
  const far = fontSize * 0.9 + fontSize * 0.45;
  const w = Math.max(fontSize, measure(text, `${fontSize * 0.75}px Arial`));
  return { w: Math.ceil(w) + 2, h: Math.ceil(near + far), glyphFromNear: near };
}

export interface DonchianMarker {
  time: number;       // timestamp in ms
  price: number;      // candle high or low
  isLong: boolean;    // true = long (below candle), false = short (above candle)
  volumeRatio: number;
  trendAligned: boolean;
}

interface DonchianMarkerRendererData {
  x: number;
  y: number;
  isLong: boolean;
  volumeRatio: number;
  trendAligned: boolean;
}

class DonchianMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: DonchianMarkerRendererData[] = [];
  private _fontSize: number = 11;

  update(data: DonchianMarkerRendererData[], fontSize: number): void {
    this._data = data;
    this._fontSize = fontSize;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledFontSize = this._fontSize * pixelRatio;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const marker of this._data) {
        const x = marker.x * pixelRatio;
        const y = marker.y * pixelRatio;

        // Dim against-trend markers slightly
        ctx.globalAlpha = marker.trendAligned ? 1.0 : 0.6;

        // Triangle color
        const color = marker.isLong ? '#34d399' : '#f87171';
        ctx.fillStyle = color;

        // Draw triangle symbol (small, slick)
        ctx.font = `${scaledFontSize}px Arial`;
        const symbol = marker.isLong ? '\u25B2' : '\u25BC'; // ▲ ▼
        ctx.fillText(symbol, x, y);

        // Volume ratio text below (for long) or above (for short)
        ctx.fillStyle = '#cbd5e1';
        ctx.font = `${scaledFontSize * 0.75}px Arial`;
        const textOffset = marker.isLong ? scaledFontSize * 0.9 : -scaledFontSize * 0.9;
        ctx.fillText(`${marker.volumeRatio.toFixed(1)}x`, x, y + textOffset);
      }

      ctx.globalAlpha = 1.0;
    });
  }
}

class DonchianMarkerPaneView implements IPrimitivePaneView {
  private _source: DonchianMarkerPrimitive;
  private _renderer: DonchianMarkerPaneRenderer;

  constructor(source: DonchianMarkerPrimitive) {
    this._source = source;
    this._renderer = new DonchianMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    this._renderer.update(this._source.getRendererData(), this._source.getFontSize());
    return this._renderer;
  }
}

export class DonchianMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: DonchianMarker[] = [];
  private _paneView: DonchianMarkerPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _fontSize: number = 11;
  private _offset: number = 16; // slightly tighter than PV's 20
  private _layout = new MarkerLayoutClient('donchian', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new DonchianMarkerPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: fixed offset alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    this._markers.forEach((marker, i) => {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) return;
      const x = ctx.timeToX(t);
      const y = ctx.priceToY(marker.price);
      if (x === null || y === null) return;
      const box = triangleTextBox(this._fontSize, `${marker.volumeRatio.toFixed(1)}x`, ctx.measure);
      out.push({
        key: `${t}:${marker.isLong ? 'l' : 's'}:${i}`,
        time: t,
        x,
        side: marker.isLong ? 'below' : 'above',
        anchorY: y,
        w: box.w,
        h: box.h,
        gap: 3,
        priority: LAYOUT_PRIORITY.donchian,
        color: marker.isLong ? '#34d399' : '#f87171',
        label: `Donchian ${marker.isLong ? 'long' : 'short'} ${marker.volumeRatio.toFixed(1)}x`,
        ref: { marker, glyphFromNear: box.glyphFromNear },
      });
    });
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setMarkers(markers: DonchianMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setFontSize(size: number): void {
    this._fontSize = size;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  getFontSize(): number {
    return this._fontSize;
  }

  getRendererData(): DonchianMarkerRendererData[] {
    if (!this._series || !this._chart) return [];

    const shared = this._layout.placed();
    if (shared) {
      return shared.map((p) => {
        const { marker, glyphFromNear } = p.item.ref as { marker: DonchianMarker; glyphFromNear: number };
        const y = marker.isLong ? p.top + glyphFromNear : p.top + p.h - glyphFromNear;
        return { x: p.item.x, y, isLong: marker.isLong, volumeRatio: marker.volumeRatio, trendAligned: marker.trendAligned };
      });
    }
    const timeScale = this._chart.timeScale();
    const data: DonchianMarkerRendererData[] = [];

    for (const marker of this._markers) {
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      const priceY = this._series.priceToCoordinate(marker.price);
      if (priceY === null) continue;

      const y = marker.isLong
        ? priceY + this._offset
        : priceY - this._offset;

      data.push({
        x,
        y,
        isLong: marker.isLong,
        volumeRatio: marker.volumeRatio,
        trendAligned: marker.trendAligned,
      });
    }

    return data;
  }
}
